import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { isOnboardingFlagSet } from "../../utils/onboardingFlags";
import moment from "moment";

// Flag para evitar múltiplos redirecionamentos
let isLoggedOut = false;

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  // Sinal autoritativo do backend: alguma rota respondeu 402 ERR_LICENSE_EXPIRED.
  // Não depende do cálculo de vencimento no cliente.
  const [licenseExpired, setLicenseExpired] = useState(false);

  const clearLicenseExpired = useCallback(() => setLicenseExpired(false), []);

  // Função para verificar se o token JWT está expirado
  const isTokenExpired = useCallback((token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      return true; // Se não for um JWT válido, considere expirado
    }
  }, []);

  // Função de logout centralizada.
  // options.expired distingue sessão expirada (mostra aviso na tela de login)
  // de logout manual (sai limpo, sem aviso). Chamadores de UI passam o evento
  // de clique como primeiro argumento, por isso o check explícito === true.
  const handleLogout = useCallback((options) => {
    if (isLoggedOut) return;
    isLoggedOut = true;

    const expired = options?.expired === true;

    setIsAuth(false);
    setUser({});
    localStorage.removeItem("token");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userId");
    localStorage.removeItem("cshow");
    delete api.defaults.headers.Authorization;

    setLoading(false); // ← Essa é a chave!

    // Páginas públicas (ex: agendamento em /agenda/:slug) não exigem login:
    // um token velho/expirado no navegador não deve arrancar o visitante
    // dessas páginas para a tela de login.
    if (!window.location.pathname.startsWith("/agenda/")) {
      history.push(expired ? "/login?expired=true" : "/login");
    }
  }, [history]);

  // Intercepta requisições
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
        setIsAuth(true);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Intercepta respostas (trata 401/403)
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Se já tentou refresh ou já está saindo, ignora
      if (isLoggedOut) {
        return Promise.reject(error);
      }

      // Tentativa de refresh token
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const { data } = await api.post("/auth/refresh_token");
          const newToken = data.token;

          localStorage.setItem("token", JSON.stringify(newToken));
          api.defaults.headers.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          return api(originalRequest);
        } catch (refreshError) {
          handleLogout({ expired: true });
          return Promise.reject(refreshError);
        }
      }

      // Se falhar mesmo com refresh ou for 401 direto
      if (error?.response?.status === 401) {
        handleLogout({ expired: true });
      }

      // Licença vencida (sinal autoritativo do backend). Marca o estado para o
      // layout exibir a tela de bloqueio, sem navegar/recarregar (evita loop).
      if (
        error?.response?.status === 402 &&
        error?.response?.data?.error === "ERR_LICENSE_EXPIRED"
      ) {
        setLicenseExpired(true);
      }

      return Promise.reject(error);
    }
  );

  // Validação inicial ao montar o hook
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");

      // Se não tem token, vai direto para login
      if (!token) {
        setLoading(false);
        setIsAuth(false);
        return;
      }

      // Verifica expiração do JWT antes de tentar refresh
      if (isTokenExpired(token)) {
        handleLogout({ expired: true });
        return;
      }

      try {
        const { data } = await api.post("/auth/refresh_token");
        const { token: newToken, user: userData } = data;

        let connections = [];
        try {
          const connRes = await api.get("/whatsapp");
          connections = connRes.data.whatsapp || [];
        } catch (err) {
          // Licença vencida bloqueia /whatsapp com 402. Nesse caso NÃO deslogar:
          // mantemos a sessão para o usuário chegar ao app e ser redirecionado
          // para /blocked pela camada de layout. Só deslogamos em outros erros.
          if (err?.response?.status !== 402) {
            handleLogout({ expired: true });
            return;
          }
        }

        const userWithConnections = { ...userData, connections };

        localStorage.setItem("token", JSON.stringify(newToken));
        api.defaults.headers.Authorization = `Bearer ${newToken}`;
        setUser(userWithConnections);
        setIsAuth(true);
      } catch (err) {
        handleLogout({ expired: true }); // Garante logout limpo
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [handleLogout]);

  // Detecta quando o usuário volta para a aba
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuth) {
        const token = localStorage.getItem("token");
        if (!token) {
          handleLogout({ expired: true });
          return;
        }

        // Verifica se o token JWT expirou. O aviso de sessão expirada é
        // exibido inline pela tela de login (via ?expired=true), sem toast.
        if (isTokenExpired(token)) {
          handleLogout({ expired: true });
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuth, handleLogout]);

  // Socket (mantido como estava)
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (companyId && user.id) {
      const socket = socketConnection({ companyId });

      socket.on(`company-${companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser((prevUser) => ({ ...prevUser, ...data.user }));
        }
      });

      return () => {
        socket.disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Login
  const handleLogin = async (userData) => {
    setLoading(true);
    isLoggedOut = false; // Reseta no login
    setLicenseExpired(false);

    try {
      const { data } = await api.post("/auth/login", userData);
      const { user: userDataResponse, token } = data;
      const { companyId, id, profile } = userDataResponse;

      localStorage.setItem("token", JSON.stringify(token));
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("userId", id);
      api.defaults.headers.Authorization = `Bearer ${token}`;

      // companyId/userId já gravados acima — usa a flag POR CONTA para
      // decidir o destino. Assim todo primeiro acesso (mobile e desktop)
      // de um admin cai no dashboard, onde ficam as boas-vindas e o tour.
      const isFirstLogin = !isOnboardingFlagSet("me_welcome_modal_seen");
      let initialPath = "/tickets";
      if (profile === "admin") {
        initialPath = isFirstLogin ? "/" : "/tickets";
      } else if (profile === "user") {
        initialPath = "/tickets";
      } else {
        throw new Error("Perfil desconhecido");
      }

      setUser(userDataResponse);
      setIsAuth(true);

      // Sem toast de sucesso: o redirecionamento já é o feedback do login.
      history.push(initialPath);
    } catch (err) {
      let errorMsg = "auth.toasts.error";
      if (err.response?.status === 401) {
        errorMsg = "auth.toasts.invalidCredentials";
      } else if (err.response?.status === 403) {
        errorMsg = "auth.toasts.accessDenied";
      } else if (err.message === "Network Error") {
        errorMsg = "auth.toasts.networkError";
      }

      toastError(i18n.t(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar info do usuário (opcional)
  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (err) {
      toastError(err);
    }
  };

  return {
    isAuth,
    user,
    loading,
    licenseExpired,
    clearLicenseExpired,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
  };
};

export default useAuth;